import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import { CreateOrderPayload, CreateOrderResponse, SavePaymentPayload, SavePaymentResponse, WalletData, WalletTransactionSearchParams, WalletTransactionSearchResponse } from "../../types/paymentTypes";

// ── Step 1: Create Razorpay order ────────────────────────────────────────────

export const createWalletOrder = createAsyncThunk<
  CreateOrderResponse,
  CreateOrderPayload,
  { rejectValue: string }
>(
  "wallet/createOrder",
  async (payload, thunkAPI) => {
    try {
      const response = await customFetch.post(
        "/api/razorpay/create/order",
        { amount: payload.amount }
      );

      let data = response.data;
      if (typeof data?.data === "string") data.data = JSON.parse(data.data);

      return data.data as CreateOrderResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.message || "Failed to create order");
    }
  }
);

// ── Step 2: Save payment result to DB ────────────────────────────────────────

export const saveWalletPayment = createAsyncThunk<
  SavePaymentResponse,
  SavePaymentPayload,
  { rejectValue: string }
>(
  "wallet/savePayment",
  async (payload, thunkAPI) => {
    try {
      const response = await customFetch.post(
        "/api/razorpay/payment",
        payload
      );

      let data = response.data;
      if (typeof data?.data === "string") data.data = JSON.parse(data.data);

      return data.data as SavePaymentResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.message || "Failed to save payment");
    }
  }
);


export const fetchWalletTransactions = createAsyncThunk<
  WalletTransactionSearchResponse,
  WalletTransactionSearchParams | undefined,
  { rejectValue: string }
>(
  "wallet/fetchTransactions",
  async (params, thunkAPI) => {
    try {
      const page = params?.page ?? 0;
      const size = params?.size ?? 10;

      const body: Record<string, any> = {
        userId: params?.filters?.userId,
      };
      if (params?.filters?.type) body.type = params.filters.type;
      if (params?.filters?.fromDate) body.fromDate = params.filters.fromDate;
      if (params?.filters?.paymentId) body.paymentId = params.filters.paymentId;
      if (params?.filters?.toDate) body.toDate = params.filters.toDate;

      const response = await customFetch.post(
        `/api/wallet-transactions/search?page=${page}&size=${size}`,
        body
      );

      let data = response.data;
      if (typeof data?.data === "string") data.data = JSON.parse(data.data);

      return {
        results: data.data.results,
        page: data.data.page,
      } as WalletTransactionSearchResponse;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.message || "Failed to fetch wallet transactions");
    }
  }
);

export const fetchWalletDetails = createAsyncThunk<
  WalletData,
  number,
  { rejectValue: string }
>(
  "wallet/fetchDetails",
  async (userId, thunkAPI) => {
    try {
      const response = await customFetch.get(
        `/api/wallet-transactions/user/${userId}`
      );

      let data = response.data;
      if (typeof data?.data === "string") data.data = JSON.parse(data.data);

      return data.data as WalletData;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch wallet details"
      );
    }
  }
);
